import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useKiosk } from '../lib/kioskStore';

/**
 * The kiosk lock, enforced at the route level (vendor#1420).
 *
 * What it renders:
 *   - kiosk mode active (`useKiosk().activeKiosk` set) → `<Navigate to="/kiosk" replace />`
 *   - otherwise                                        → children, untouched
 *
 * Shell: wraps staff routes only — the authenticated `/` tree, `/subscribe`,
 * `/invite/:token`, the `/m` redirect table and the top-level 404. It must NEVER wrap `/kiosk`, or the
 * kiosk would redirect to itself forever.
 *
 * Why it exists: kiosk keeps the real staff Supabase session (vendor#742), and
 * `RequireAuth` checks only that session. Before this guard, the only thing
 * between the customer browse and the whole staff app was that nothing on a
 * kiosk screen happened to navigate out — a deep link, browser Back, a typed
 * URL or a surviving notice button all opened staff screens with no PIN. Now
 * the one way back is the PIN exit, which clears `activeKiosk` BEFORE it
 * navigates to `/` (`ExitKioskModal.leave` → `KioskLayout.handleExit`).
 *
 * Non-obvious contracts:
 *   - Mounted OUTSIDE `RequireAuth`, so a locked device never bootstraps or
 *     renders any staff shell, not even its loading skeleton.
 *   - `replace`, so Back from `/kiosk` onto a staff entry swaps that entry for
 *     `/kiosk` rather than stacking a new one; repeated Back walks the history
 *     down, redirecting each time, and never loops.
 *   - Reload: `activeKiosk` is persisted to `localStorage` and the store's
 *     storage is synchronous, so Zustand rehydrates it when the module loads —
 *     before the first render. A reload on a staff URL therefore redirects on
 *     the first paint, with no flash of staff UI.
 *   - Other tabs: `lib/kioskStore.ts` rehydrates on another tab's `storage`
 *     write (vendor#1428), so entering kiosk in one tab locks every open tab.
 *   - Entry paths (`EnterKioskModal`, the idle lock in `useIdleKioskEntry`)
 *     call `enterKiosk()` then `navigate('/kiosk')` in the same tick. Either
 *     the explicit navigate wins or this redirect does; both end on `/kiosk`,
 *     and nothing here navigates anywhere else, so there is no race to lose.
 *   - `/sign-in`, `/sign-up` and `/reset-password` are deliberately NOT
 *     wrapped. They show no staff data, and they are the recovery path when
 *     the staff session dies under a locked tablet: `verify_kiosk_pin` is
 *     granted to `authenticated` only, so without a session the PIN exit
 *     cannot succeed. `ExitKioskModal` then offers "Sign in again", in that
 *     state only (vendor#1427). Signing back in lands on `/`, which this guard
 *     sends to `/kiosk`, where the PIN exit works again. Signing in never
 *     opens a staff screen while locked.
 */
export interface KioskLockGuardProps {
  children: ReactNode;
}

export function KioskLockGuard({ children }: KioskLockGuardProps) {
  const locked = useKiosk((s) => s.activeKiosk !== null);
  if (locked) return <Navigate to="/kiosk" replace />;
  return <>{children}</>;
}
